import { mapRelatedPreferenceDataToGetClientPreferencesData } from '@/app/(main)/client-detail/components/tabs/RelatedTravelersInformation/utils/mapRelatedPreferenceDataToGetClientPreferencesData';
import type {
  GetClientData,
  GetClientPreferencesData,
  RelatedPassengerPreferencesData,
} from '@/types/clientsApiTypes';
import type { GetClientDataWithAdditionalData } from '../../../types';

/**
 * Related travelers use {@link RelatedPassengerPreferencesData} (fly/personal/diet, no Stay tab).
 * Primary client preferences include `stay`, `travel`, lifestyle arrays, etc.
 *
 * Previously we only checked `beverage_preferences`; some API payloads omit it while still being
 * primary — then we wrongly ran `mapRelatedPreferenceDataToGetClientPreferencesData` and **dropped
 * `stay`**, so the read-only panel showed legacy accommodation tags but edit modals opened empty.
 */
function preferenceDataIsPrimaryClientShape(data: unknown): data is GetClientPreferencesData {
  if (typeof data !== 'object' || data === null) {
    return false;
  }

  const d = data as Record<string, unknown>;

  if ('stay' in d || 'travel' in d) {
    return true;
  }

  if ('beverage_preferences' in d && Array.isArray(d.beverage_preferences)) {
    return true;
  }

  return (
    'pillow_preferences' in d ||
    'room_configuration_preferences' in d ||
    'spa_preferences' in d ||
    'spa_preferences_special_requests' in d ||
    'likes' in d ||
    'dislikes' in d
  );
}

export function mapClientForDefaultFormData(
  client?: GetClientDataWithAdditionalData
): GetClientData | undefined {
  if (client == null) {
    return undefined;
  }

  const { important_dates: _importantDates, passport: _passport, ...rest } = client;

  const preferenceData = rest.preferences?.preference_data;
  if (
    rest.preferences != null &&
    preferenceData != null &&
    !preferenceDataIsPrimaryClientShape(preferenceData)
  ) {
    return {
      ...rest,
      preferences: {
        ...rest.preferences,
        preference_data: mapRelatedPreferenceDataToGetClientPreferencesData(
          preferenceData as RelatedPassengerPreferencesData
        ),
      },
    } as GetClientData;
  }

  return rest as GetClientData;
}
